import React, { useState, useEffect } from 'react';
import { AppData } from '../types';
import { AppIcon } from '../components/AppIcon';
import { GrowthScoreGauge, getGrowthClassification } from '../components/GrowthScoreGauge';
import { appCatalog, STOREFRONT_COUNTRIES } from '../services/appCatalogService';
import { CATEGORIES_LIST } from '../data/mockData';
import { Radar, Sparkles, TrendingUp, Zap, Star, ShieldCheck, Globe, Loader2, ArrowUpRight } from 'lucide-react';

interface LaunchRadarViewProps {
  onSelectApp: (appId: string) => void;
}

export const LaunchRadarView: React.FC<LaunchRadarViewProps> = ({ onSelectApp }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState<string>(appCatalog.getSelectedCountry());
  const [radarApps, setRadarApps] = useState<AppData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [provenance, setProvenance] = useState<string>('AppStatsView Derived Intelligence');

  useEffect(() => {
    fetchRadar();
  }, [selectedCategory, selectedCountry]);

  const fetchRadar = async () => {
    setIsLoading(true);
    try {
      const response = await appCatalog.getLaunchRadar({
        category: selectedCategory,
        country: selectedCountry,
        limit: 20
      });
      setRadarApps(response.results);
      if (response.provenance) setProvenance(response.provenance);
    } catch (err) {
      console.error('[LaunchRadarView] Error loading launch radar:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatReleaseDate = (dateStr?: string) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const diffDays = Math.max(0, Math.floor((Date.now() - date.getTime()) / (24 * 3600 * 1000)));
    if (diffDays === 0) return 'Launched Today';
    if (diffDays === 1) return 'Launched Yesterday';
    return `Day ${diffDays} since launch`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Top Banner */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border border-purple-200/60 dark:border-purple-800/40">
                  <Radar className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 animate-spin-slow" />
                  Launch Radar Intelligence
                </span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                  <ShieldCheck className="w-3 h-3 text-blue-500" />
                  {provenance}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                Launch Radar
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                Early breakout detection for newly released applications showing exceptional ranking momentum, rating velocity, and initial review growth.
              </p>
            </div>

            {/* Filter Controls */}
            <div className="flex items-center gap-2">
              <select
                value={selectedCategory}
                onChange={e => setSelectedCategory(e.target.value)}
                aria-label="Filter launch radar by category"
                className="px-3 py-2 text-xs font-medium bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-slate-900 dark:text-white"
              >
                <option value="all">All Categories</option>
                {CATEGORIES_LIST.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>

              <select
                value={selectedCountry}
                onChange={e => setSelectedCountry(e.target.value)}
                aria-label="Filter launch radar by storefront country"
                className="px-3 py-2 text-xs font-medium bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-slate-900 dark:text-white"
              >
                {STOREFRONT_COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-3" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Scanning recent launches for early breakout signals...</p>
          </div>
        ) : radarApps.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
            <Radar className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">No breakout signals detected</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
              Our continuous engine is ingesting new releases around the clock. Check back as new observations accumulate.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {radarApps.map((app, index) => {
              const growth = getGrowthClassification(app.growthScore || 65);
              return (
                <div
                  key={app.id || app.appStoreId}
                  onClick={() => onSelectApp(String(app.appStoreId || app.id))}
                  className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 hover:border-purple-500/50 dark:hover:border-purple-500/50 hover:shadow-md transition-all duration-200 cursor-pointer"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left: App Info */}
                    <div className="flex items-start gap-4">
                      <div className="relative">
                        <AppIcon
                          src={app.iconUrl}
                          alt={app.name}
                          size={64}
                          className="rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800"
                        />
                        <span className="absolute -top-2 -left-2 w-6 h-6 rounded-full bg-purple-600 text-white font-bold text-xs flex items-center justify-center shadow">
                          #{index + 1}
                        </span>
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-base text-slate-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                            {app.name}
                          </h3>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/40">
                            <Sparkles className="w-3 h-3" />
                            {formatReleaseDate(app.firstReleaseDate)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {app.developer} • <span className="font-medium text-slate-700 dark:text-slate-300">{app.category}</span>
                        </p>

                        {/* Signal Summary */}
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
                          <div className="flex items-center gap-1 text-slate-700 dark:text-slate-300">
                            <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                            <span className="font-semibold">{(app.rating || 4.5).toFixed(1)}</span>
                            <span className="text-slate-400">({(app.totalReviews || app.ratingCount || 0).toLocaleString()} reviews)</span>
                          </div>
                          <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400 font-medium">
                            <Zap className="w-3.5 h-3.5" />
                            <span>Search Position #{app.currentRank || app.searchPosition || 18}</span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                            <Globe className="w-3.5 h-3.5" />
                            <span>{app.country || selectedCountry} Store</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Growth Score & Action */}
                    <div className="flex items-center gap-4 self-end md:self-center">
                      <div className="text-right">
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">Growth Score</div>
                        <div className="text-lg font-bold text-purple-600 dark:text-purple-400">
                          {app.growthScore || 78}/100
                        </div>
                        <div className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                          {growth.category}
                        </div>
                      </div>

                      <button className="flex items-center gap-1 px-4 py-2 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors shadow-sm">
                        <span>Profile</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
